import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '@/App';
import DashboardLayout from '../components/DashboardLayout';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { MessageCircle, TrendingUp, DollarSign, Users, Phone, CheckCircle, Plus } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const PIPELINE_STAGES = ['nuevo', 'contactado', 'cotizacion', 'venta', 'perdido'];
const STAGE_COLORS = {
  nuevo: 'bg-blue-100 text-blue-700',
  contactado: 'bg-yellow-100 text-yellow-700',
  cotizacion: 'bg-purple-100 text-purple-700',
  venta: 'bg-green-100 text-green-700',
  perdido: 'bg-red-100 text-red-700'
};

export default function WhatsAppROI() {
  const { token } = useContext(AuthContext);
  const { clientId } = useWorkspace();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddLead, setShowAddLead] = useState(false);
  const [filter, setFilter] = useState('todos');
  const [newLead, setNewLead] = useState({
    phone: '', campaign_name: '', vendedor: '', pipeline_status: 'nuevo', valor_venta: '', notas: ''
  });

  const headers = { Authorization: `Bearer ${token}` };

  const fetchLeads = async () => {
    try {
      const r = await axios.get(`${API}/whatsapp/leads?client_id=${clientId}`, { headers });
      setLeads(r.data || []);
    } catch(e) {}
    setLoading(false);
  };

  useEffect(() => { if (clientId) fetchLeads(); }, [clientId]);

  const addLead = async () => {
    try {
      await axios.post(`${API}/whatsapp/leads`, { ...newLead, client_id: clientId }, { headers });
      setShowAddLead(false);
      setNewLead({ phone: '', campaign_name: '', vendedor: '', pipeline_status: 'nuevo', valor_venta: '', notas: '' });
      fetchLeads();
    } catch(e) {}
  };

  const updateStage = async (id, stage) => {
    try {
      await axios.patch(`${API}/whatsapp/leads/${id}`, { pipeline_status: stage }, { headers });
      fetchLeads();
    } catch(e) {}
  };

  const filtered = filter === 'todos' ? leads : leads.filter(l => l.pipeline_status === filter);
  const totalLeads = leads.length;
  const ventas = leads.filter(l => l.pipeline_status === 'venta').length;
  const revenue = leads.filter(l => l.pipeline_status === 'venta').reduce((s, l) => s + (parseFloat(l.valor_venta) || 0), 0);
  const closeRate = totalLeads > 0 ? ((ventas / totalLeads) * 100).toFixed(1) : 0;

  return (
    <DashboardLayout>
      <div className="p-6 max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <MessageCircle className="w-7 h-7 text-green-500" />
              WhatsApp ROI
            </h1>
            <p className="text-gray-500 text-sm mt-1">Trackea leads, conversiones y ROI desde WhatsApp</p>
          </div>
          <button onClick={() => setShowAddLead(true)} className="flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg font-medium text-sm">
            <Plus className="w-4 h-4" /> Agregar Lead
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Total Leads', value: totalLeads, icon: <Users className="w-5 h-5 text-blue-500" />, color: 'text-blue-600' },
            { label: 'Ventas Cerradas', value: ventas, icon: <CheckCircle className="w-5 h-5 text-green-500" />, color: 'text-green-600' },
            { label: 'Revenue', value: `$${revenue.toLocaleString()}`, icon: <DollarSign className="w-5 h-5 text-purple-500" />, color: 'text-purple-600' },
            { label: 'Tasa de Cierre', value: `${closeRate}%`, icon: <TrendingUp className="w-5 h-5 text-orange-500" />, color: 'text-orange-600' },
          ].map((kpi, i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">{kpi.label}</p>
                {kpi.icon}
              </div>
              <p className={`text-2xl font-bold ${kpi.color}`}>{kpi.value}</p>
            </div>
          ))}
        </div>

        <div className="flex gap-2 mb-6 flex-wrap">
          {['todos', ...PIPELINE_STAGES].map(s => (
            <button key={s} onClick={() => setFilter(s)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium capitalize transition-all ${filter === s ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
              {s} {s !== 'todos' && `(${leads.filter(l => l.pipeline_status === s).length})`}
            </button>
          ))}
        </div>

        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          {loading ? (
            <div className="p-12 text-center text-gray-400">Cargando leads...</div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center">
              <MessageCircle className="w-12 h-12 text-gray-200 mx-auto mb-3" />
              <p className="text-gray-400 font-medium">No hay leads todavia</p>
              <p className="text-gray-300 text-sm mt-1">Agrega tu primer lead manualmente</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  {['Telefono', 'Campana', 'Vendedor', 'Estado', 'Valor', 'Notas', 'Accion'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.map(lead => (
                  <tr key={lead.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-green-500" />
                        <span className="text-sm font-medium text-gray-900">{lead.phone || '-'}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{lead.campaign_name || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{lead.vendedor || '-'}</td>
                    <td className="px-4 py-3">
                      <select value={lead.pipeline_status} onChange={e => updateStage(lead.id, e.target.value)}
                        className={`text-xs font-semibold px-2 py-1 rounded-full border-0 cursor-pointer ${STAGE_COLORS[lead.pipeline_status]}`}>
                        {PIPELINE_STAGES.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                      {lead.valor_venta ? `$${parseFloat(lead.valor_venta).toLocaleString()}` : '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 max-w-xs truncate">{lead.notas || '-'}</td>
                    <td className="px-4 py-3">
                      <a href={`https://wa.me/${lead.phone}`} target="_blank" rel="noreferrer"
                        className="text-green-500 hover:text-green-600 text-xs font-medium">Abrir WA</a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {showAddLead && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md">
              <h3 className="font-bold text-lg text-gray-900 mb-4">Agregar Lead</h3>
              <div className="space-y-3">
                {[
                  { key: 'phone', label: 'Telefono', placeholder: '+56912345678' },
                  { key: 'campaign_name', label: 'Campana', placeholder: 'Nombre campana Meta' },
                  { key: 'vendedor', label: 'Vendedor', placeholder: 'Nombre vendedor' },
                  { key: 'valor_venta', label: 'Valor venta ($)', placeholder: '0' },
                  { key: 'notas', label: 'Notas', placeholder: 'Observaciones...' },
                ].map(f => (
                  <div key={f.key}>
                    <label className="text-xs font-medium text-gray-500 uppercase">{f.label}</label>
                    <input value={newLead[f.key]} onChange={e => setNewLead({...newLead, [f.key]: e.target.value})}
                      placeholder={f.placeholder}
                      className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
                  </div>
                ))}
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">Estado</label>
                  <select value={newLead.pipeline_status} onChange={e => setNewLead({...newLead, pipeline_status: e.target.value})}
                    className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none">
                    {PIPELINE_STAGES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button onClick={() => setShowAddLead(false)} className="flex-1 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50">Cancelar</button>
                <button onClick={addLead} className="flex-1 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg text-sm font-medium">Guardar Lead</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
